import React, { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Card, CardBody } from "reactstrap";

import CardHeader from "../Layout/CardHeader";
import SuperInsightsModal from "../Ia/SuperInsightsModal";
import Meta from '../Layout/Meta';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const BarChartY = ({ item }) => {
  const safeItem = item || {};

  let {
    Sigla = '',
    Descricao_Dashboard_Componente: titulo = "-",
    dadosComponente = {},
    Valor_Meta = {},
    Texto_Info = "-",
    Texto_BizAI = "",
    ESG_Info = "-",
    Codigo_Transacao_Link = "",
    Titulo_Transacao_Link = "",
    opcoesComponente,
  } = safeItem;

  // Parse seguro dos dados/opções (string -> objeto) e memoização
  const parsedData = useMemo(() => {
    try {
      const raw = safeItem?.dadosComponente;
      return typeof raw === 'string' ? JSON.parse(raw) : (raw || {});
    } catch (_) { return {}; }
  }, [safeItem?.dadosComponente]);

  const parsedOptions = useMemo(() => {
    try {
      const raw = safeItem?.opcoesComponente;
      return (typeof raw === 'string' ? JSON.parse(raw) : raw) || {};
    } catch (_) { return {}; }
  }, [safeItem?.opcoesComponente]);

  // Monta dados sem mutar props e com limites de espessura para estabilidade
  const chartData = useMemo(() => {
    const srcLabels = Array.isArray(parsedData?.labels) ? [...parsedData.labels] : [];
    const srcDatasets = Array.isArray(parsedData?.datasets) ? parsedData.datasets : [];
    const labels = srcLabels.length ? srcLabels : srcDatasets.map(ds => ds?.label || '');
    const datasets = srcDatasets.map(ds => {
      const realData = Array.isArray(ds?.data) ? ds.data.map(v => Number(v || 0)) : [];
      const pct = Number(ds?.percentage || 0);
      return {
        ...ds,
        realData,
        data: [pct],
        borderWidth: 0,
        barThickness: 24,
        maxBarThickness: 28,
        minBarLength: 2,
        categoryPercentage: 0.6,
        barPercentage: 0.5,
      };
    });
    return { labels, datasets };
  }, [parsedData]);

  const hasData = useMemo(() => {
    const anyPct = chartData.datasets.some(ds => (ds.data || []).some(v => Number(v || 0) !== 0));
    const anyLabel = (chartData.labels || []).some(l => String(l || '').trim().length > 0);
    return anyPct && anyLabel;
  }, [chartData]);

  const chartOptions = useMemo(() => ({
    ...parsedOptions,
    indexAxis: parsedOptions?.indexAxis || 'y',
    responsive: true,
    resizeDelay: 150,
    maintainAspectRatio: false,
    animation: false,
    layout: { padding: { bottom: 8 } },
    plugins: {
      ...(parsedOptions?.plugins || {}),
      legend: {
        ...(parsedOptions?.plugins?.legend || {}),
        display: true,
        fullSize: false,
        position: 'bottom',
        labels: {
          ...(parsedOptions?.plugins?.legend?.labels || {}),
          font: {
            family: 'Poppins, sans-serif',
            size: 10,
            weight: '400',
            color: '#000'
          },
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        ...(parsedOptions?.plugins?.tooltip || {}),
        mode: 'nearest',
        intersect: true,
        backgroundColor: '#fff',
        borderColor: '#ccc',
        borderWidth: 1,
        titleColor: '#666',
        bodyColor: '#000',
        displayColors: true,
        usePointStyle: true,
        callbacks: {
          title: items => items[0].label,
          label: ctx => {
            const real = ctx.dataset.realData?.[ctx.dataIndex] ?? 0;
            return [
              `${ctx.dataset.label}:`,
              `Valor: ${Number(real).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`
            ];
          },
          afterLabel: ctx => {
            const pct = ctx.dataset.percentage ?? 0;
            return `Percentual: ${pct.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
          },
          labelColor: ctx => ({
            borderColor: 'transparent',
            backgroundColor: ctx.dataset.backgroundColor,
            width: 10,
            height: 10,
            borderRadius: 5
          })
        }
      }
    },
    scales: {
      x: {
        ...(parsedOptions?.scales?.x || {}),
        min: 0,
        max: 100,
        title: {
          ...((parsedOptions?.scales?.x || {}).title || {}),
          display: false
        },
        ticks: {
          ...((parsedOptions?.scales?.x || {}).ticks || {}),
          callback: v => `${v}%`,
          font: { family: 'Poppins, sans-serif', weight: '400', size: 10 }
        }
      },
      y: {
        ...(parsedOptions?.scales?.y || {}),
        ticks: {
          ...((parsedOptions?.scales?.y || {}).ticks || {}),
          display: false,
          font: { family: 'Poppins, sans-serif', weight: '600', size: 12 }
        }
      }
    }
  }), [parsedOptions]);

  // Controle do Modal
 const [isPopupOpen, setIsPopupOpen] = useState(false);
 const togglePopup = () => setIsPopupOpen(!isPopupOpen);;
 const slugId = Sigla.trim().replace(/\s+/g, "-");
  
  return (
    <>
    <Card className="card-dash-item w-100 h-100">    
      <CardBody className="d-flex flex-column">
        <CardHeader
            titulo={titulo}
            Texto_Info={Texto_Info}
            ESG_Info={ESG_Info}
            togglePopup={togglePopup}
            slugId={slugId}
            textoIA={Texto_BizAI}
            Codigo_Transacao_Link={Codigo_Transacao_Link}
            Titulo_Transacao_Link={Titulo_Transacao_Link}
        />

          {/* Metas e Valores */}       
          <Meta Valor_Meta={Valor_Meta} />
        
       
          <div
            style={{
              marginTop: "6px",
              height: "100%",
            }}
          >
            {hasData ? (
              <Bar data={chartData} options={chartOptions} redraw={false} updateMode="none" />
            ) : (
              <div className="w-100 h-100 d-flex align-items-center justify-content-center text-muted" style={{ fontSize: 14 }}>
                Sem dados para exibir
              </div>
            )}
          </div>
        
      </CardBody>
    </Card>
      {/* Modal de Insights */}
      <SuperInsightsModal
        isOpen={isPopupOpen}
        toggle={togglePopup}
        textoIA={Texto_BizAI} // Passamos somente o texto da pergunta
      />
    </>
  );
};

export default BarChartY;
